import { createRouter, createWebHistory } from 'vue-router'

const OrdersView = () => import('@/views/OrdersView.vue')
const NewOrderView = () => import('@/views/NewOrderView.vue')
const AddressBookView = () => import('@/views/AddressBookView.vue')
const TicketView = () => import('@/views/TicketView.vue')
const CalendarView = () => import('@/views/CalendarView.vue')
const ScheduleView = () => import('@/views/ScheduleView.vue')
const BuildingView = () => import('@/views/BuildingView.vue')
const BuildingDetailView = () => import('@/views/BuildingDetailView.vue')
const ScheduleTicket = () => import('@/components/ScheduleTicket.vue')
const ScheduleStats = () => import('@/components/ScheduleStats.vue')
const PlannerView = () => import('@/views/PlannerView.vue')
const PlannerTasks = () => import('@/components/planner/PlannerTasks.vue')
const PlannerGroups = () => import('@/components/planner/PlannerGroups.vue')
const PlannerGroup = () => import('@/components/planner/PlannerGroup.vue')
const PlannerCapacity = () => import('@/components/planner/PlannerCapacity.vue')
const PlannerProcesses = () =>
  import('@/components/planner/PlannerProcesses.vue')
const PlannerProcess = () => import('@/components/planner/PlannerProcess.vue')
const PlannerGroupCreate = () =>
  import('@/components/planner/PlannerGroupCreate.vue')
const PlannerOverview = () => import('@/components/planner/PlannerOverview.vue')
const PlannerTask = () => import('@/components/planner/PlannerTask.vue')
const PlannerSales = () => import('@/components/planner/PlannerSales.vue')
const PlannerSavedFiltersPage = () =>
  import('@/components/planner/PlannerSavedFiltersPage.vue')

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/v2/',
      redirect: '/v2/tickets',
    },
    {
      path: '/v2/logistics',
      name: 'logistics',
      component: () => import('@/views/LogisticsView.vue'),
      meta: {
        title: 'Logistiek - NORM',
      },
      children: [
        {
          path: '',
          redirect: { name: 'items-overview' },
        },
        {
          path: 'items',
          name: 'items-view',
          component: () => import('@/views/ItemsView.vue'),
          children: [
            {
              path: '',
              redirect: { name: 'items-overview' },
            },
            {
              path: 'overview',
              name: 'items-overview',
              component: () => import('@/components/items/ItemsOverview.vue'),
            },
            {
              path: 'lists',
              name: 'item-lists',
              component: () => import('@/components/items/ItemLists.vue'),
            },
            {
              path: 'lists/:id',
              name: 'item-list-details',
              component: () => import('@/components/items/ItemListDetails.vue'),
            },
            {
              path: 'view/:code',
              name: 'item-detail',
              component: () => import('@/components/items/ItemDetails.vue'),
            },
            {
              path: 'serienummers',
              name: 'serial-numbers',
              component: () => import('@/components/items/SerialNumbers.vue'),
            },
            {
              path: 'serienummers/detail',
              name: 'serial-number-detail',
              component: () =>
                import('@/components/items/SerialNumberDetail.vue'),
            },
          ],
        },
        {
          path: 'stock',
          name: 'stock-view',
          component: () => import('@/views/StockView.vue'),
          children: [
            {
              path: '',
              redirect: { name: 'warehouse-stock-overview' },
            },
            {
              path: 'overview',
              name: 'warehouse-stock-overview',
              component: () => import('@/components/stock/StockOverview.vue'),
            },
            {
              path: 'movements',
              name: 'warehouse-stock-movements',
              component: () => import('@/components/stock/StockMovements.vue'),
            },
          ],
        },
        {
          path: 'orders',
          name: 'orders-list',
          component: OrdersView,
          meta: {
            title: 'Logistiek - NORM',
          },
        },
      ],
    },
    // Old standalone paths now live under /v2/logistics; keep redirects for bookmarks.
    {
      path: '/v2/orders',
      redirect: { name: 'orders-list' },
    },
    {
      path: '/v2/new-order',
      name: 'new-order',
      component: NewOrderView,
    },
    {
      path: '/v2/tickets',
      name: 'tickets-list',
      component: TicketView,
    },
    {
      path: '/v2/tickets/:id',
      name: 'ticket-detail',
      component: TicketView,
    },
    {
      path: '/v2/tickets/:id/images/:image',
      name: 'ticket-view-images',
      component: TicketView,
    },
    {
      path: '/v2/calendar',
      name: 'calendar',
      component: CalendarView,
    },
    {
      path: '/v2/address-book',
      name: 'address-book',
      component: AddressBookView,
    },
    {
      path: '/v2/planner',
      name: 'planner',
      component: PlannerView,
      meta: {
        title: 'S&OP - NORM',
      },
      children: [
        {
          path: 'tasks',
          name: 'planner-tasks',
          component: PlannerTasks,
        },
        {
          path: 'groups',
          name: 'planner-groups',
          component: PlannerGroups,
        },
        {
          path: 'groups/comments/:id',
          name: 'planner-groups-comments',
          component: PlannerGroups,
        },
        {
          path: 'groups/:id',
          name: 'planner-group-details',
          component: PlannerGroup,
        },
        {
          path: 'process',
          name: 'planner-processes',
          component: PlannerProcesses,
        },
        {
          path: 'process/:id',
          name: 'planner-process-details',
          component: PlannerProcess,
        },
        {
          path: 'capacity',
          name: 'planner-capacity',
          component: PlannerCapacity,
        },
        {
          path: 'create-group',
          name: 'planner-group-create',
          component: PlannerGroupCreate,
        },
        {
          path: 'overview',
          name: 'planner-overview',
          component: PlannerOverview,
        },
        {
          path: 'tasks/:id',
          name: 'planner-task-details',
          component: PlannerTask,
        },
        {
          path: 'sales',
          name: 'planner-sales',
          component: PlannerSales,
        },
        {
          path: 'filters',
          name: 'planner-filters',
          component: PlannerSavedFiltersPage,
        },
        {
          path: 'default-conversions',
          name: 'planner-default-conversions',
          component: () =>
            import('@/components/planner/PlannerDefaultConversions.vue'),
        },
      ],
    },
    {
      path: '/v2/schedule/:year?/:month?',
      name: 'schedule',
      component: ScheduleView,
      children: [
        {
          path: 'ticket/:id',
          name: 'schedule-ticket',
          component: ScheduleTicket,
        },
        {
          path: 'stats/:week?',
          name: 'schedule-stats',
          component: ScheduleStats,
        },
      ],
    },
    {
      path: '/v2/buildings',
      name: 'buildings-list',
      component: BuildingView,
    },
    {
      path: '/v2/buildings/:id',
      name: 'building-details',
      component: BuildingDetailView,
    },
    {
      // The IPAs section: the partners, and the prijsopgaven Norm handles with them.
      path: '/v2/partners',
      component: () => import('@/views/InstallationPartnersView.vue'),
      meta: {
        title: 'Installatie Partners - NORM',
      },
      children: [
        {
          path: '',
          name: 'installation-partner-list',
          component: () => import('@/views/InstallationPartnerList.vue'),
        },
        {
          path: 'quotes',
          name: 'ipa-quotes',
          component: () => import('@/components/installerQuotes/QuoteList.vue'),
          meta: { title: 'IPA prijsopgaven - NORM' },
        },
        {
          path: 'quotes/:id',
          name: 'ipa-quote-detail',
          component: () =>
            import('@/components/installerQuotes/QuoteDetail.vue'),
          meta: { title: 'IPA prijsopgave - NORM' },
        },
        {
          // After the static siblings: Vue Router ranks a literal segment over a
          // dynamic one, so /v2/partners/quotes is not a partner.
          path: ':id',
          name: 'installation-partner-details',
          component: () => import('@/views/InstallationPartnerDetails.vue'),
        },
      ],
    },
    {
      // Reached from the approval mail, by someone who is not logged in. The token in
      // the path is the whole of the authorisation; see services/quote_approvals.py.
      path: '/v2/quote-approval/:token',
      name: 'quote-approval',
      component: () => import('@/views/QuoteApprovalView.vue'),
      meta: {
        title: 'Offerte goedkeuren - NORM',
        public: true,
      },
    },
    {
      // The partner's own entry: their offers, their menu item. Norm reaches the same
      // screens through the IPAs section above.
      path: '/v2/installer-quotes',
      name: 'installer-quotes',
      component: () => import('@/components/installerQuotes/QuoteList.vue'),
      meta: { title: 'Prijsopgaven - NORM' },
    },
    {
      path: '/v2/installer-quotes/:id',
      name: 'installer-quote-detail',
      component: () => import('@/components/installerQuotes/QuoteDetail.vue'),
      meta: { title: 'Prijsopgave - NORM' },
    },
    {
      path: '/v2/customers',
      component: () => import('@/views/CustomersView.vue'),
      meta: {
        title: 'Commercie - NORM',
      },
      children: [
        {
          path: '',
          redirect: { name: 'customer-list' },
        },
        {
          path: 'list',
          name: 'customer-list',
          component: () => import('@/views/CustomerList.vue'),
        },
        {
          path: 'prices',
          name: 'price-overview',
          component: () => import('@/components/customers/PriceOverview.vue'),
        },
        {
          path: 'price-logs',
          name: 'price-change-logs',
          component: () => import('@/components/customers/PriceChangeLogs.vue'),
        },
        {
          path: 'quotes',
          name: 'customer-quotes',
          component: () => import('@/components/customerQuotes/QuoteList.vue'),
        },
        {
          path: 'quotes/:id',
          name: 'customer-quote-detail',
          component: () =>
            import('@/components/customerQuotes/QuoteDetail.vue'),
        },
        {
          // A child of /v2/customers rather than a route of its own, so the section's
          // tab bar in CustomersView stays visible while looking at one klant. The
          // static siblings above still win: Vue Router ranks a literal segment over a
          // dynamic one, so /v2/customers/list is the list and not a klant called
          // "list".
          path: ':id',
          name: 'customer-details',
          component: () => import('@/views/CustomerDetails.vue'),
        },
      ],
    },
    {
      path: '/v2/stock/:pathMatch(.*)*',
      redirect: { name: 'stock-view' },
    },
    {
      path: '/v2/projects',
      name: 'projects-list',
      component: () => import('@/views/ProjectsView.vue'),
      meta: {
        title: 'Projecten - NORM',
      },
    },
    {
      path: '/v2/energieleveren',
      name: 'energieleveren',
      component: () => import('@/views/EnergieleverenView.vue'),
      meta: {
        title: 'Energieleveren.nl - NORM',
      },
    },
    {
      // The Energize activation form for IPAs without a ticket (EH-1857).
      path: '/v2/meter-activation',
      name: 'meter-activation',
      component: () => import('@/views/MeterActivationView.vue'),
      meta: {
        title: 'Meter activatie - NORM',
      },
    },
    {
      path: '/v2/projects/new',
      name: 'create-project',
      component: () => import('@/views/CreateProjectView.vue'),
      meta: {
        title: 'Nieuw project - NORM',
      },
    },
    {
      path: '/v2/projects/:id',
      name: 'project-details',
      component: () => import('@/views/ProjectDetailView.vue'),
      meta: {
        title: 'Project - NORM',
      },
    },
    {
      path: '/v2/items/:pathMatch(.*)*',
      redirect: { name: 'items-view' },
    },
    {
      path: '/v2/offers',
      name: 'offers',
      component: () => import('@/components/offers/OffersList.vue'),
      meta: {
        title: 'Overeenkomst - NORM',
      },
    },
    {
      path: '/v2/finished-tasks',
      name: 'finished-tasks',
      component: () => import('@/views/FinishedTasksView.vue'),
      meta: {
        title: 'Finished tasks - NORM',
      },
    },
    {
      path: '/v2/users',
      name: 'users-list',
      component: () => import('@/views/UsersView.vue'),
      meta: {
        title: 'Gebruikers - NORM',
      },
    },
    {
      path: '/v2/users/service-accounts',
      name: 'service-accounts',
      component: () => import('@/views/ServiceAccountsView.vue'),
      meta: {
        title: 'Service accounts - NORM',
      },
    },
    {
      path: '/v2/users/permissions',
      name: 'permissions-admin',
      component: () => import('@/views/PermissionsView.vue'),
      meta: {
        title: 'Rechtenbeheer - NORM',
      },
    },
    {
      path: '/v2/users/permissions/:kind/new',
      name: 'permission-create',
      component: () => import('@/views/PermissionDetailView.vue'),
      meta: {
        title: 'Rechtenbeheer - NORM',
      },
    },
    {
      path: '/v2/users/permissions/:kind/:name',
      name: 'permission-detail',
      component: () => import('@/views/PermissionDetailView.vue'),
      meta: {
        title: 'Rechtenbeheer - NORM',
      },
    },
    {
      path: '/v2/users/:username',
      name: 'user-detail',
      component: () => import('@/views/UserDetailView.vue'),
      meta: {
        title: 'Gebruiker - NORM',
      },
    },
  ],
  scrollBehavior(to, from, savedPosition) {
    if (
      (to.name === 'ticket-detail' && from.name !== 'ticket-view-images') ||
      to.name === 'schedule-ticket'
    ) {
      return { top: 0 }
    }
    return savedPosition
  },
})

router.beforeEach((to, from, next) => {
  const parent = to.matched[to.matched.length - 2]
  if (to.meta.title) {
    document.title = to.meta.title
  } else if (parent && parent.meta && parent.meta.title) {
    document.title = parent.meta.title
  } else {
    document.title = 'Tickets - NORM'
  }
  next()
})

export default router
